import { useQuery } from "@tanstack/react-query";
import type { PageData } from "../api-schema";
import {API_BASE_DOMAIN, REACT_QUERY_GC_TIME, REACT_QUERY_STALE_TIME} from "../constants";
import {queryClient} from "../query-client";
import {getImagesFromPageData} from "../image-loader";
import {getUserLanguage} from "../user";

export type APIPageName =
    'home' |
    'sponsors' |
    'province' |
    'city' |
    'language' |
    'instructions' |
    'character' |
    's_station1_A' |
    's_station1_B' |
    's_communication_A' |
    's_communication_B' |
    's_relationship_A' |
    's_relationship_B' |
    's_4fashion_A' |
    's_4fashion_B' |
    's_4geek_A' |
    's_4geek_B' |
    's_4music_A' |
    's_4music_B' |
    's_4sport_A' |
    's_4sport_B' |
    's_end_A' |
    's_end_B' |
    's_end_C' |
    's_end_D';

export enum APIPagesName {
    HOME = "home",
    SPONSORS = "sponsors",
    PROVINCE = "province",
    CITY = "city",
    LANGUAGE = "language",
    INSTRUCTIONS = "instructions",
    CHARACTER = "character",
    STATION1_A = "s_station1_A",
    STATION1_B = "s_station1_B",
    STATION_COMMUNICATION_A = "s_communication_A",
    STATION_COMMUNICATION_B = "s_communication_B",
    STATION_RELATIONSHIP_A = "s_relationship_A",
    STATION_RELATIONSHIP_B = "s_relationship_B",
    STATION_4FASHION_A = "s_4fashion_A",
    STATION_4FASHION_B = "s_4fashion_B",
    STATION_4GEEK_A = "s_4geek_A",
    STATION_4GEEK_B = "s_4geek_B",
    STATION_4MUSIC_A = "s_4music_A",
    STATION_4MUSIC_B = "s_4music_B",
    STATION_4SPORT_A = "s_4sport_A",
    STATION_4SPORT_B = "s_4sport_B",
    STATION_END_A = "s_end_A",
    STATION_END_B = "s_end_B",
    STATION_END_C = "s_end_C",
    STATION_END_D = "s_end_D",
}

// Hook to fetch all page data on app load
export function useAllPageData() {
    return useQuery({
        queryKey: ["/api/page-data"],
        queryFn: async (): Promise<PageData[]> => {
            const response = await fetch(API_BASE_DOMAIN + "/api/page-data");
            if (!response.ok) {
                throw new Error("Failed to fetch page data");
            }
            return response.json();
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}

// Hook to fetch specific page data
export function usePageData(pageName: APIPageName) {
    return useQuery({
        queryKey: ["/api/page-data", pageName],
        queryFn: async (): Promise<PageData> => {
            const response = await fetch(`${API_BASE_DOMAIN}/api/page-data/${pageName}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch page data for ${pageName}`);
            }
            return response.json();
        },
        gcTime: REACT_QUERY_GC_TIME,
        staleTime: REACT_QUERY_STALE_TIME,

    });
}

// Prefetch specific page data
export async function prefetchPage(pageName: string): Promise<PageData> {
    return queryClient.fetchQuery({
        queryKey: ["/api/page-data", pageName],
        queryFn: async (): Promise<PageData> => {
            const response = await fetch(`${API_BASE_DOMAIN}/api/page-data/${pageName}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch page data for ${pageName}`);
            }
            return response.json();
        },
        gcTime: REACT_QUERY_GC_TIME,
        staleTime: REACT_QUERY_STALE_TIME,
    });
}

/**
 * Prefetch images by creating Image() elements (cached in browser)
 */
export async function prefetchPageImages(
    imageUrls: string[],
    options?: { limit_asset?: number } //Number of images to preload (default = all)
): Promise<void> {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return;

    const { limit_asset: limit } = options || {};
    const urlsToLoad = typeof limit === "number" ? imageUrls.slice(0, limit) : imageUrls;

    await queryClient.prefetchQuery({
        queryKey: ["page-images", urlsToLoad],
        queryFn: async () => {
            const promises = urlsToLoad.map((url) => {
                return new Promise<void>(async (resolve) => {
                    // Create a new Image object to preload the image
                    const img = new Image();
                    img.onload = () => resolve();
                    img.onerror = () => resolve(); // ignore failures
                    img.src = url;
                    console.log('image preloaded', img)
                });
            });
            await Promise.all(promises);
            return urlsToLoad;
        },
        gcTime: REACT_QUERY_GC_TIME,
        staleTime: REACT_QUERY_STALE_TIME,
    });
}

/**
 * Combined helper to preload both page data and images.
 */
export async function prefetchPageAndAssets(
    pageName: APIPageName,
    options?: { limit_asset?: number; } //Number of images to preload (default = all)
): Promise<void> {
    try {
        const lang = getUserLanguage();
        const pageData = await prefetchPage(pageName);

        const images = getImagesFromPageData(pageData, lang);

        await prefetchPageImages(images, { limit_asset: options?.limit_asset });
    } catch (err) {
        console.error(`❌ Prefetch failed for ${pageName}:`, err);
    }
}

// Helper function to get image URL based on language
export function getImageUrlFromPageData(
    pageData: PageData | undefined,
    language: string,
    imageIndex: number = 0
): string {
    if (!pageData) return "";

    let images: string[] = [];

    switch (language) {
        case "it":
            images = pageData.screenViewImagesUrlItalian || [];
            break;
        case "en":
            images = pageData.screenViewImagesUrlEnglish || [];
            break;
        case "de":
            images = pageData.screenViewImagesUrlGerman || [];
            break;
        default:
            images = pageData.screenViewImagesUrlEnglish || [];
    }

    return images[imageIndex] || "";
}

// Helper function to get hint images based on language
export function getHintImagesFromPageData(
    pageData: PageData | undefined,
    language: string
): string[] {
    if (!pageData) return [];

    switch (language) {
        case "it":
            return pageData.hintsImagesUrlItalian || [];
        case "en":
            return pageData.hintsImagesUrlEnglish || [];
        case "de":
            return pageData.hintsImagesUrlGerman || [];
        default:
            return pageData.hintsImagesUrlEnglish || [];
    }
}